import { NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';

import { duration } from 'moment/moment';
import { WINDOW } from 'ngx-window-token';
import { formatBytes } from '../../helpers/format-bytes.helper';

// how often to update speed and time estimate
const UPDATE_INTERVAL = 1000;
// smoothing factor for speed and time estimate
const SMOOTHING_FACTOR = 0.05;

// html entities would be escaped, at least within matTooltip
const INFO_SEPARATOR = '  |  ';

@Component({
  selector: 'gsm-upload-progress',
  templateUrl: './upload-progress.component.html',
  styleUrls: ['./upload-progress.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    MatTooltip,
    NgTemplateOutlet,
    MatProgressBar,
    MatButton,
    MatIcon,
    MatIconButton
  ]
})
export class UploadProgressComponent implements OnChanges,
                                                OnDestroy {
  private window = inject<Window>(WINDOW);

  @Input() bytesLoaded = 0;
  @Input() bytesTotal = 0;
  @Input() fileName: string;
  @Input() condensed = false;
  @Output() cancelEvent = new EventEmitter();

  bytesPerSecond = 0;
  secondsRemaining = 0;

  private _timerId = 0;

  ngOnDestroy() {
    if (this._timerId) {
      clearInterval(this._timerId);
    }
  }

  ngOnChanges({bytesLoaded}: SimpleChanges) {
    if (!this._timerId && bytesLoaded?.currentValue) {
      // Set a timer to recalculate speed and time remaining at regular intervals.
      this._timerId = this.window.setInterval(() => {
        // If it's reached 100%, reset estimations and stop updating
        if (this.bytesLoaded === this.bytesTotal) {
          this._resetEstimations();
          return;
        }

        // bytesLoaded is not expected to decrease, but this deals with that scenario just in case.
        const previousBytesLoaded =
          bytesLoaded.previousValue <= bytesLoaded.currentValue ? bytesLoaded.previousValue : bytesLoaded.currentValue;

        const updateIntervalSeconds = UPDATE_INTERVAL / 1000;
        const currentBytesPerSecond = (this.bytesLoaded - previousBytesLoaded) / updateIntervalSeconds;
        // Use exponential moving average to estimate speed without jumping around. See https://stackoverflow.com/a/3841706/2577276
        // If previous bytesPerSecond is 0, directly set to currentBytesPerSecond.
        this.bytesPerSecond = this.bytesPerSecond
          ? SMOOTHING_FACTOR * currentBytesPerSecond + (1 - SMOOTHING_FACTOR) * this.bytesPerSecond
          : currentBytesPerSecond;

        // Note: if upload never starts, bytesPerSecond stays at 0, and secondsRemaining is Infinity! That's OK. :D
        this.secondsRemaining = Math.round((this.bytesTotal - this.bytesLoaded) / this.bytesPerSecond);
      }, UPDATE_INTERVAL);
    } else if (bytesLoaded && !bytesLoaded.currentValue) {
      // If bytesLoaded was reset to 0, reset estimations and stop updating
      this._resetEstimations();
    }
  }

  get formattedProgressInfo(): string {
    return `${formatBytes(this.bytesLoaded, 2)} von ${formatBytes(this.bytesTotal, 2)} ${INFO_SEPARATOR} ${formatBytes(
      this.bytesPerSecond,
      2
    )}/s ${INFO_SEPARATOR} Restzeit: ${
      this.secondsRemaining >= 60 ? duration(this.secondsRemaining, 'seconds') : this.secondsRemaining
    } Sekunden`;
  }

  private _resetEstimations() {
    clearInterval(this._timerId);
    this._timerId = 0;
    this.bytesPerSecond = 0;
    this.secondsRemaining = 0;
  }

  get percent(): number {
    return Math.round((this.bytesLoaded / this.bytesTotal) * 100);
  }
}
